import * as React from 'react'
import styled from 'styled-components'

import * as E from 'fp-ts/lib/Either'
import { pipe } from 'fp-ts/lib/pipeable'

import { LocalDate } from '@espoon-voltti/ts-libs/lib/time'
import {
  Title,
  Button,
  Columns,
  Column
} from '@espoon-voltti/react-components/elements'
import { Section } from '@espoon-voltti/react-components/layout'
import { LabelValueItem } from '@espoon-voltti/react-components/components'

import { LabelValueList } from '@espoon-voltti/react-voltti/components/label-value-list'

import { t } from '../../../i18n'
import { getEditOrgLink } from '../../../utils/service-url-providers'
import { formatOrgAreas } from '../../../utils/value-formatters'
import { Org } from '@espoon-voltti/nuora-domain/applicants'

const createOrgInfo = (a: Org): LabelValueItem[] => [
  {
    dataQa: 'name',
    label: t(['org', 'name', 'label']),
    value: a.name
  },
  {
    dataQa: 'orgType',
    label: t(['org', 'type', 'label']),
    value: t(['org', 'type', a.type])
  },
  {
    dataQa: 'domicile',
    label: t(['org', 'domicile', 'label']),
    value: a.domicile
  },
  {
    dataQa: 'areas',
    label: t(['org', 'areas', 'label']),
    value: formatOrgAreas(a.areas)
  },
  {
    dataQa: 'businessId',
    label: t(['org', 'businessId', 'label']),
    value: a.registrationId
  },
  {
    dataQa: 'registrationDate',
    label: t(['org', 'registrationDate', 'label']),
    value: a.registrationDate
      ? pipe(
          LocalDate.parse(a.registrationDate),
          E.map((x) => x.format()),
          E.getOrElse(() => '')
        )
      : ''
  },
  {
    dataQa: 'postalAddress',
    label: t(['org', 'address', 'postalAddress', 'label']),
    value: a.address.postalAddress
  },
  {
    dataQa: 'postalCode',
    label: t(['org', 'address', 'postalCode', 'label']),
    value: a.address.postalCode
  },
  {
    dataQa: 'postOffice',
    label: t(['org', 'address', 'postOffice', 'label']),
    value: a.address.postOffice
  },
  {
    dataQa: 'email',
    label: t(['org', 'email', 'label']),
    value: a.email
  },
  {
    dataQa: 'wwwAddress',
    label: t(['org', 'wwwAddress', 'label']),
    value: a.wwwAddress
  },
  {
    dataQa: 'bankAccount',
    label: t(['org', 'bankAccount', 'label']),
    value: a.bankAccount
  }
]

const createPreviewOrgInfo = (a: Org): LabelValueItem[] => [
  ...createOrgInfo(a),
  {
    dataQa: 'requiredSignersAmount',
    label: t(['org', 'requiredSignersAmount', 'label']),
    value: a.requiredSignersAmount
  }
]

const createRepresentativeInfo = (a: Org): LabelValueItem[] =>
  a.representatives.map((r) => ({
    dataQa: r.role,
    label: t(['org', 'representativeRole', r.role]),
    value: (
      <>
        <div>
          {r.firstName} {r.lastName}
        </div>
        <span>{r.email}</span>
      </>
    )
  }))

interface State {
  org: LabelValueItem[]
  representatives: LabelValueItem[]
}

interface Props {
  org: Org
}

export const OrgInfo = ({ org }: Props) => {
  const [state, setState] = React.useState<State>({
    org: [],
    representatives: []
  })

  React.useEffect(() => {
    setState({
      org: createPreviewOrgInfo(org),
      representatives: createRepresentativeInfo(org)
    })
  }, [org])

  return (
    <>
      <Section>
        <Columns>
          <Column>
            <LabelValueList dataQa={'orgInfo'} values={state.org} />
          </Column>
        </Columns>
      </Section>
      <Section>
        <Title size={3}>{t(['org', 'representatives', 'label'])}</Title>
        <Columns>
          <Column>
            <LabelValueList
              dataQa={'representatives'}
              values={state.representatives}
            />
          </Column>
        </Columns>
      </Section>
    </>
  )
}
export const OrgInfoWithEdit = ({ org }: Props) => {
  const [state, setState] = React.useState<State>({
    org: [],
    representatives: []
  })

  React.useEffect(() => {
    setState({
      org: createOrgInfo(org),
      representatives: createRepresentativeInfo(org)
    })
  }, [org])

  return (
    <>
      <Section>
        <Columns>
          <Column>
            <LabelValueList dataQa={'orgInfo'} values={state.org} />
          </Column>
          <Column size={2}>
            <a href={getEditOrgLink(org.id)} tabIndex={-1}>
              <EditButton dataQa={'editOrg'}>
                {t(['common', 'edit'])}
              </EditButton>
            </a>
          </Column>
        </Columns>
      </Section>
      <Section>
        <Title size={3}>{t(['org', 'representatives', 'label'])}</Title>
        <Columns>
          <Column>
            <LabelValueList
              dataQa={'representatives'}
              values={state.representatives}
            />
          </Column>
          <Column size={2} />
        </Columns>
      </Section>
    </>
  )
}

const EditButton = styled(Button)`
  position: relative;
  width: 150px;
  margin-top: 1em;
  margin-left: 2em;
`
